(() => {
  const preference = matchMedia('(prefers-reduced-motion: reduce)');
  const running = new Set();
  const seen = new WeakSet();
  const ease = 'cubic-bezier(.22, 1, .36, 1)';
  const play = (element, frames, options = {}) => {
    if (!element?.animate || preference.matches || document.hidden) return Promise.resolve();
    const animation = element.animate(frames, { duration: 360, easing: ease, ...options });
    running.add(animation);
    return animation.finished.catch(() => {}).finally(() => running.delete(animation));
  };
  const enter = (element, options = {}) => play(element, [
    { opacity: 0, transform: `translateY(${options.y ?? 12}px)` },
    { opacity: 1, transform: 'translateY(0)' }
  ], options);
  const exit = (element, options = {}) => play(element, [
    { opacity: 1, transform: 'translateY(0)' },
    { opacity: 0, transform: `translateY(${options.y ?? 8}px)` }
  ], { duration: 160, ...options });
  window.CrafiiMotion = { enter, exit, reduced: () => preference.matches };
  preference.addEventListener('change', () => {
    if (preference.matches) running.forEach(animation => animation.cancel());
  });
  document.addEventListener('pointerdown', event => {
    running.forEach(animation => {
      if (animation.effect?.target?.contains(event.target)) animation.finish();
    });
  }, { passive: true });
  document.addEventListener('focusin', event => {
    running.forEach(animation => {
      if (animation.effect?.target?.contains(event.target)) animation.finish();
    });
  });

  const selectors = [
    'main h1', 'main h2', 'main h3',
    '.crafii-color-studio__image', '.crafii-color-studio__copy > *',
    '.crafii-studio-category', '.crafii-studio-card', '.crafii-category-rail__item',
    '.crafii-studio-story__copy > *', '.crafii-review', '.crafii-ideas__item',
    'main .title-wrapper-with-link > *', 'main .collection-hero__description',
    'main .main-page-title + .rte > *', 'main .rte > p', 'main .rte > ul',
    'main .rte > figure', 'main .rte > table', 'main .rte > div',
    'main .product__media-wrapper', 'main .product__info-container > *',
    'main .crafii-studio-purchase-facts', 'main .accordion',
    '.cf-packs__intro > *', '.cf-pack__photos', '.cf-pack__body > *',
    '.cf-packs__shipping', '.cf-packs__details > div:first-child > *',
    '.cf-packs__faq > details', '.cf-packs__browse',
    '.shopify-policy__title', '.shopify-policy__body > *',
    '.cf-footer-columns > div', '.crafii-signup > *',
    'main .image-with-text__media', 'main .image-with-text__content > *',
    'main .multicolumn-card', 'main .article-card-wrapper', 'main .blog-articles__article'
  ].join(',');
  const groups = '.crafii-studio-category, .crafii-studio-card, .crafii-category-rail__item, .crafii-review, .crafii-ideas__item, .product__media-wrapper, .cf-pack__photos, .accordion, .cf-packs__faq > details, .multicolumn-card, .article-card-wrapper';
  const pending = new Map();
  const reveal = (element, delay = 0) => {
    const animation = pending.get(element);
    if (!animation) return;
    pending.delete(element);
    observer?.unobserve(element);
    if (preference.matches || document.hidden) {
      animation.cancel();
      element.dataset.cfReveal = 'complete';
      return;
    }
    element.dataset.cfReveal = 'revealing';
    animation.effect.updateTiming({ delay });
    animation.play();
    running.add(animation);
    animation.finished.catch(() => {}).finally(() => {
      animation.cancel();
      running.delete(animation);
      element.dataset.cfReveal = 'complete';
    });
  };
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    const visible = entries.filter(entry => entry.isIntersecting).sort((a, b) => {
      const aBox = a.boundingClientRect;
      const bBox = b.boundingClientRect;
      return Math.abs(aBox.top - bBox.top) < 45 ? aBox.left - bBox.left : aBox.top - bBox.top;
    });
    visible.forEach((entry, index) => reveal(entry.target, Math.min(index * 90, 360)));
  }, { threshold: 0.08, rootMargin: '0px 0px -5% 0px' }) : null;
  const register = root => {
    const targets = [...(root.matches?.(selectors) ? [root] : []), ...root.querySelectorAll(selectors)];
    targets.forEach(element => {
      if (seen.has(element)) return;
      const ancestor = element.parentElement?.closest(groups);
      if (ancestor || element.closest('dialog, product-modal, cart-drawer, .cf-packs__mobile-cart') || element.parentElement?.closest('details:not([open])')) return;
      const box = element.getBoundingClientRect();
      if (!box.width || !box.height || element.matches('.visually-hidden, [aria-live], .crafii-add-status')) return;
      seen.add(element);
      // The page already has one CSS arrival fade. Never hide visible content
      // again when this deferred script or a native section update runs.
      if (box.top < innerHeight || preference.matches || !observer || !element.animate) {
        element.dataset.cfReveal = 'complete';
        return;
      }
      const image = element.matches('img, .cf-pack__photos, .product__media-wrapper, .image-with-text__media');
      const animation = element.animate([
        { opacity: 0, transform: element.matches('.crafii-color-studio__image') ? 'translateY(22px) scale(1.025)' : 'translateY(22px)' },
        { opacity: 1, transform: 'none' }
      ], { duration: image ? 1100 : 900, easing: ease, fill: 'both' });
      animation.pause();
      animation.currentTime = 0;
      element.dataset.cfReveal = 'pending';
      pending.set(element, animation);
      observer.observe(element);
    });
  };
  const finishContaining = target => {
    pending.forEach((animation, element) => {
      if (element.contains(target)) {
        pending.delete(element);
        observer?.unobserve(element);
        animation.cancel();
        element.dataset.cfReveal = 'complete';
      }
    });
  };
  document.addEventListener('focusin', event => finishContaining(event.target));
  document.addEventListener('pointerdown', event => finishContaining(event.target), { passive: true });
  preference.addEventListener('change', () => {
    if (preference.matches) [...pending.keys()].forEach(element => reveal(element));
  });
  register(document);
  document.addEventListener('shopify:section:load', event => register(event.target));
  const updates = new MutationObserver(records => {
    records.forEach(record => record.addedNodes.forEach(node => {
      if (node.nodeType === 1 && node.closest('main')) register(node);
    }));
    pending.forEach((animation, element) => {
      if (!element.isConnected) {
        animation.cancel();
        pending.delete(element);
        observer?.unobserve(element);
      }
    });
  });
  document.querySelector('main') && updates.observe(document.querySelector('main'), { childList: true, subtree: true });
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      running.forEach(animation => animation.cancel());
      [...pending.keys()].forEach(element => {
        const box = element.getBoundingClientRect();
        if (box.top < innerHeight && box.bottom > 0) reveal(element);
      });
    }
  });

  document.addEventListener('toggle', event => {
    const details = event.target;
    if (!details.matches?.('details[open]') || details.closest('details-modal, menu-drawer, .crafii-studio-menu')) return;
    const content = [...details.children].find(child => child.tagName !== 'SUMMARY');
    enter(content, { duration: 220, y: 5 });
  }, true);

  const policy = document.getElementById('CrafiiShipping');
  if (policy) {
    let opener;
    let closing = false;
    const closePolicy = () => {
      if (!policy.open || closing) return;
      closing = true;
      policy.classList.add('cf-policy-dialog--closing');
      exit(policy).finally(() => {
        policy.close();
        policy.classList.remove('cf-policy-dialog--closing');
        document.body.classList.remove('cf-policy-open');
        closing = false;
        if (opener?.isConnected) opener.focus({ preventScroll: true });
      });
    };
    document.addEventListener('click', event => {
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
      const destination = new URL(link.href, location.href);
      if (destination.origin !== location.origin || destination.pathname !== policy.dataset.policyPath) return;
      if (policy.open || closing || document.querySelector('dialog[open]')) return;
      event.preventDefault();
      opener = link;
      policy.showModal();
      document.body.classList.add('cf-policy-open');
      policy.querySelector('[data-policy-close]').focus({ preventScroll: true });
      enter(policy, { duration: 280, y: 10 });
    });
    policy.querySelector('[data-policy-close]').addEventListener('click', closePolicy);
    policy.addEventListener('cancel', event => { event.preventDefault(); closePolicy(); });
    policy.addEventListener('click', event => {
      if (event.target !== policy) return;
      const box = policy.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closePolicy();
    });
  }

  document.querySelectorAll('[data-category-rail]').forEach(rail => {
    const track = rail.querySelector('[data-category-track]');
    const previous = rail.querySelector('[data-category-previous]');
    const next = rail.querySelector('[data-category-next]');
    const update = () => {
      previous.disabled = track.scrollLeft < 2;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };
    const move = direction => track.scrollBy({
      left: direction * Math.max(160, track.clientWidth * .75),
      behavior: preference.matches ? 'instant' : 'smooth'
    });
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    track.addEventListener('scroll', update, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
    update();
    rail.classList.add('is-ready');
  });
})();
